/**
 * React Query hooks for batch data management
 *
 * Provides hooks for fetching and mutating batch data with
 * automatic cache invalidation and optimistic updates.
 *
 * Includes SSE support for real-time batch statistics updates.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { batchService } from '../services/api';
import {
  Batch,
  BatchCreate,
  BatchCSVUpload,
  BatchUpdate,
  BatchListResponse,
  BatchFilters,
} from '../types/batch';

const BATCHES_QUERY_KEY = 'batches';

/**
 * Hook to fetch list of batches with filters
 */
export function useBatches(filters?: BatchFilters): UseQueryResult<BatchListResponse, Error> {
  return useQuery({
    queryKey: [BATCHES_QUERY_KEY, filters],
    queryFn: () => batchService.list(filters),
    // Keep data fresh for 1 minute
    staleTime: 60000,
  });
}

/**
 * Hook to fetch a single batch by ID with statistics
 */
export function useBatch(id: string | undefined): UseQueryResult<Batch, Error> {
  return useQuery({
    queryKey: [BATCHES_QUERY_KEY, id],
    queryFn: () => batchService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 60000,
  });
}

/**
 * SSE status for batch updates
 */
export type BatchSSEStatus = 'connecting' | 'open' | 'closed' | 'error';

/**
 * Batch statistics from SSE stream
 */
export interface BatchStats {
  batch_id: string;
  status: string;
  total_jobs: number;
  raw_jobs: number;
  parsing_jobs: number;
  parsed_jobs: number;
  parse_failed_jobs: number;
  running_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  queued_jobs: number;
  jobs_queued_count?: number;
  success_rate: number;
  updated_at: string | null;
  // Evaluation fields
  pass_count?: number;
  fail_count?: number;
  error_count?: number;
  na_count?: number;
}

/**
 * Result from useBatchSSE hook
 */
interface UseBatchSSEResult {
  stats: BatchStats | null;
  status: BatchSSEStatus;
  error: string | null;
  reconnect: () => void;
}

/**
 * Hook for real-time batch statistics via SSE
 *
 * Connects to the batch SSE endpoint and receives periodic updates
 * every 5 seconds with job counts and status distribution.
 *
 * @param batchId - The ID of the batch to stream updates for
 * @param enabled - Whether to establish the connection (default: true)
 * @returns Object containing current stats, connection status, error, and reconnect function
 *
 * @example
 * const { stats, status, error } = useBatchSSE(batchId);
 */
export function useBatchSSE(batchId: string | undefined, enabled: boolean = true): UseBatchSSEResult {
  const [stats, setStats] = useState<BatchStats | null>(null);
  const [status, setStatus] = useState<BatchSSEStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  // Maximum reconnection attempts
  const MAX_RECONNECT_ATTEMPTS = 3;

  // Base API URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  /**
   * Close the EventSource connection and cleanup
   */
  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Establish SSE connection to backend
   */
  const connect = useCallback(() => {
    if (!batchId || !enabled) {
      return;
    }

    // Close existing connection if any
    closeConnection();

    try {
      setStatus('connecting');
      setError(null);

      // Get auth token from localStorage for SSE connection
      const token = localStorage.getItem('access_token');
      const url = `${API_BASE_URL}/api/batches/${batchId}/stream?token=${token}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = () => {
        setStatus('open');
        setReconnectAttempts(0); // Reset reconnection attempts on successful connection
      };

      // Listen for batch_stats events
      eventSource.addEventListener('batch_stats', (e: MessageEvent) => {
        try {
          const batchStats: BatchStats = JSON.parse(e.data);
          setStats(batchStats);

          // Update the React Query cache with new stats
          queryClient.setQueryData([BATCHES_QUERY_KEY, batchId], (oldData: Batch | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              status: batchStats.status,
              total_jobs: batchStats.total_jobs,
              raw_jobs: batchStats.raw_jobs,
              parsing_jobs: batchStats.parsing_jobs,
              parsed_jobs: batchStats.parsed_jobs,
              parse_failed_jobs: batchStats.parse_failed_jobs,
              running_jobs: batchStats.running_jobs,
              completed_jobs: batchStats.completed_jobs,
              failed_jobs: batchStats.failed_jobs,
              success_rate: batchStats.success_rate,
              updated_at: batchStats.updated_at,
            };
          });
        } catch (err) {
          console.error('Failed to parse batch_stats event:', err);
        }
      });

      // Listen for error events from backend
      eventSource.addEventListener('error', (e: MessageEvent) => {
        try {
          const errorData = JSON.parse(e.data);
          setError(errorData.error || 'An error occurred during batch streaming');
        } catch (err) {
          console.error('Failed to parse error event:', err);
        }
      });

      // Listen for complete event (stream finished successfully)
      eventSource.addEventListener('complete', () => {
        setStatus('closed');
        closeConnection();
      });

      // Handle connection errors
      eventSource.onerror = (e) => {
        console.error('SSE connection error:', e);
        setStatus('error');

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000); // Max 10 seconds
          setError(`Connection lost. Reconnecting in ${delay / 1000}s...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1);
            connect();
          }, delay);
        } else {
          setError('Failed to connect to batch stream after multiple attempts. Please refresh the page.');
          closeConnection();
        }
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
      setStatus('error');
      setError('Failed to establish connection to batch stream');
    }
  }, [batchId, enabled, reconnectAttempts, closeConnection, API_BASE_URL, queryClient]);

  /**
   * Manual reconnection function
   */
  const reconnect = useCallback(() => {
    setReconnectAttempts(0);
    setStats(null); // Clear existing stats on manual reconnect
    connect();
  }, [connect]);

  // Establish connection when batchId changes or enabled becomes true
  useEffect(() => {
    if (batchId && enabled) {
      connect();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      closeConnection();
    };
  }, [batchId, enabled, connect, closeConnection]);

  return {
    stats,
    status,
    error,
    reconnect,
  };
}

/**
 * Hook to create a new batch
 */
export function useCreateBatch(): UseMutationResult<Batch, Error, BatchCreate> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BatchCreate) => batchService.create(data),
    onSuccess: () => {
      // Invalidate and refetch batches list
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to create a batch from CSV upload with LLM parsing
 */
export function useCreateBatchFromCSV(): UseMutationResult<
  Batch,
  Error,
  { file: File; data: Omit<BatchCSVUpload, 'csv_content'> }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, data }: { file: File; data: Omit<BatchCSVUpload, 'csv_content'> }) => {
      // Read CSV file content
      const csvContent = await file.text();

      // Call batch service with CSV content
      return batchService.createFromCSV({
        ...data,
        csv_content: csvContent,
      });
    },
    onSuccess: () => {
      // Invalidate and refetch batches list
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an existing batch
 */
export function useUpdateBatch(): UseMutationResult<Batch, Error, { id: string; data: BatchUpdate }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BatchUpdate }) =>
      batchService.update(id, data),
    onSuccess: (updatedBatch) => {
      // Invalidate batches list
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });

      // Update the cache for the specific batch
      queryClient.setQueryData(
        [BATCHES_QUERY_KEY, updatedBatch.batch_id],
        updatedBatch
      );
    },
  });
}

/**
 * Hook to delete a batch
 */
export function useDeleteBatch(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => batchService.delete(id),
    onSuccess: () => {
      // Invalidate and refetch batches list
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}
